import { FormControl, HStack, Text, VStack } from 'components/ui';
import { useToken } from '@chakra-ui/react';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { components, ControlProps, OptionProps } from 'react-select';
import { MapPin } from 'lucide-react';

const { REACT_APP_GOOGLE_PLACES_KEY } = process.env;

const ControlWithIcon = (props: ControlProps<any, false>) => {
  const [blueGray600] = useToken('colors', ['blueGray.600']);

  return (
    <components.Control {...props}>
      <MapPin
        size={16}
        style={{ marginLeft: 8, flexShrink: 0 }}
        color={blueGray600}
      />
      {props.children}
    </components.Control>
  );
};

const AddressOption = (props: OptionProps<any, false>) => {
  const [blueGray600] = useToken('colors', ['blueGray.600']);
  const { structured_formatting } = props.data.value;
  const { main_text, secondary_text } = structured_formatting;

  return (
    <components.Option {...props}>
      <HStack gap={3}>
        <MapPin size={15} color={blueGray600} />

        <VStack gap={0.5}>
          <Text
            fontWeight={600}
            color={'secondary.500'}
            fontFamily={'body'}
            fontSize={'14.5px'}
          >
            {main_text}
          </Text>
          <Text color={'blueGray.600'} fontFamily={'body'} fontSize={'12.5px'}>
            {secondary_text}
          </Text>
        </VStack>
      </HStack>
    </components.Option>
  );
};

type Props = {
  locationInputValue: string;
  onLocationSelect: (newLocation: any) => void;
  selectedLocation: any;
  setLocationInputValue: (newLocationString: string) => void;
};

export const LocationSelect = ({
  locationInputValue,
  onLocationSelect,
  selectedLocation,
  setLocationInputValue,
}: Props) => {
  return (
    <FormControl required>
      <FormControl.Label>Street address</FormControl.Label>

      <GooglePlacesAutocomplete
        apiKey={REACT_APP_GOOGLE_PLACES_KEY}
        debounce={500}
        autocompletionRequest={{
          componentRestrictions: {
            country: ['us', 'ca'],
          },
        }}
        selectProps={{
          components: {
            IndicatorSeparator: () => null,
            DropdownIndicator: () => null,
            Control: ControlWithIcon,
            Option: AddressOption,
          },
          noOptionsMessage: () => 'No address found',
          placeholder: 'Start typing your address',
          menuIsOpen: locationInputValue.length > 0,
          inputValue: locationInputValue,
          onInputChange: setLocationInputValue,
          value: selectedLocation,
          onChange: onLocationSelect,
          styles: {
            container: (base) => ({
              ...base,
              width: '100%',
            }),
            menu: (base) => ({
              ...base,
              borderRadius: '14px',
              overflow: 'hidden',
            }),
            menuList: (base) => ({
              ...base,
              padding: 0,
            }),
            placeholder: (base) => ({
              ...base,
              fontSize: '14px',
            }),
            control: (base) => ({
              ...base,
              borderRadius: '8px',
              height: 48,
              padding: '0px 8px',
              backgroundColor: 'rgba(4, 51, 56, 0.05)',
              borderColor: 'transparent',
              boxShadow: 'none',
            }),
            option: (base, state) => ({
              ...base,
              borderBottom: '1px solid #E0E1E1',
              padding: '12px 16px',
              backgroundColor: state.isFocused
                ? 'rgba(4, 51, 56, 0.05)'
                : 'transparent',
            }),
          },
        }}
      />
    </FormControl>
  );
};
